import { useState, useCallback } from "react";
import { dashboardBackendUrl } from "../../config/gateway";

interface Logs {
  id: number;
  processName: string;
  executionStatus: string;
  creationDate: string;
  totalItems: string;
  failedItems: number;
  actions: unknown;
}

export interface LogLookFilters {
  fromDate?: string;
  toDate?: string;
  status?: string;
}

interface UseGetLogsReturn {
  datalogs: Logs[] | null;
  setDatalogs: React.Dispatch<React.SetStateAction<Logs[] | null>>;
  loading: boolean;
  error: string | null;
  totalElements: number;
  fetchLogs: (
    processType: number,
    page: number,
    size: number,
    filters?: LogLookFilters
  ) => Promise<void>;
}

const useGetLogs = (): UseGetLogsReturn => {
  const [datalogs, setDatalogs] = useState<Logs[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState<number>(0);

  const fetchLogs = useCallback(
    async (
      processType: number,
      page: number,
      size: number,
      filters: LogLookFilters = {}
    ): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const tokenStorage = localStorage.getItem("token");
        const token = tokenStorage ? tokenStorage.replace(/['"]+/g, "") : null;
        if (!token) {
          throw new Error(
            "Token de acceso no encontrado. Inicia sesión primero."
          );
        }

        const params = new URLSearchParams({
          processType: String(processType),
          fromDate: filters.fromDate ?? "",
          toDate: filters.toDate ?? "",
          status: filters.status ?? "",
          page: String(page),
          size: String(size),
        });

        const response = await fetch(
          `${dashboardBackendUrl.logLook()}?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            `Error al obtener LOGS: ${response.status} - ${response.statusText}`
          );
        }

        if (!Array.isArray(data?.logs)) {
          throw new Error(
            "Formato de respuesta inesperado. Se esperaba un array de los Logs."
          );
        }

        setDatalogs(data?.logs);
        setTotalElements(data.totalElements);
      } catch (err) {
        console.error("Error fetching logs:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Error desconocido al realizar la solicitud"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { datalogs, setDatalogs, loading, error, fetchLogs, totalElements };
};

export default useGetLogs;
